import React, { useState } from 'react';
import {
  View,
  TouchableOpacity,
  Text,
  StyleSheet,
  Platform,
  Dimensions,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useTheme } from '../../../theme';

type TabType = 'Hybrid' | 'Table' | 'Calendar';

// SVG Icon Components
const HybridIcon: React.FC<{ fillColor: string }> = ({ fillColor }) => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path d="M3.33337 5V15H17.5V5H3.33337ZM15.8334 13.3333H5.00004V10.8333H15.8334V13.3333ZM5.00004 9.16667V6.66667H15.8334V9.16667H5.00004Z" fill={fillColor}/>
  </Svg>
);

const TableIcon: React.FC<{ fillColor: string }> = ({ fillColor }) => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path d="M16.6667 2.5H4.16667C3.25 2.5 2.5 3.25 2.5 4.16667V15.8333C2.5 16.75 3.25 17.5 4.16667 17.5H16.6667C17.5833 17.5 18.3333 16.75 18.3333 15.8333V4.16667C18.3333 3.25 17.5833 2.5 16.6667 2.5ZM16.6667 4.16667V6.66667H4.16667V4.16667H16.6667ZM12.5 15.8333H8.33333V8.33333H12.5V15.8333ZM4.16667 8.33333H6.66667V15.8333H4.16667V8.33333ZM14.1667 15.8333V8.33333H16.6667V15.8333H14.1667Z" fill={fillColor}/>
  </Svg>
);

const CalendarIcon: React.FC<{ fillColor: string }> = ({ fillColor }) => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path d="M16.6666 2.49998H15.8333V0.833313H14.1666V2.49998H5.83329V0.833313H4.16663V2.49998H3.33329C2.41663 2.49998 1.66663 3.24998 1.66663 4.16665V17.5C1.66663 18.4166 2.41663 19.1666 3.33329 19.1666H16.6666C17.5833 19.1666 18.3333 18.4166 18.3333 17.5V4.16665C18.3333 3.24998 17.5833 2.49998 16.6666 2.49998ZM16.6666 17.5H3.33329V8.33331H16.6666V17.5ZM16.6666 6.66665H3.33329V4.16665H16.6666V6.66665Z" fill={fillColor}/>
  </Svg>
);

interface ActionPlanDashboardTitleProps {
  selectedTab?: TabType;
  onTabChange?: (tab: TabType) => void;
}

const ActionPlanDashboardTitle: React.FC<ActionPlanDashboardTitleProps> = ({
  selectedTab = 'Hybrid',
  onTabChange,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
  const [activeTab, setActiveTab] = useState<TabType>(selectedTab);
  const screenWidth = Dimensions.get('window').width;
  const isMobile = screenWidth < 768;
  const isTablet = screenWidth >= 768 && screenWidth < 1024;

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    onTabChange?.(tab);
  };

  // Determine title based on selected tab
  const getTitle = (): string => {
    if (activeTab === 'Hybrid' || activeTab === 'Calendar') {
      return 'My Calendar';
    }
    return 'Action Plan';
  };

  const getIconColor = (isActive: boolean): string => {
    if (isActive) return '#FFFFFF';
    return isDark ? colors.text.primary : 'rgb(3, 72, 149)';
  };

  const getIcon = (tab: TabType, isActive: boolean): React.ReactElement => {
    const fillColor = getIconColor(isActive);
    switch (tab) {
      case 'Hybrid':
        return <HybridIcon fillColor={fillColor} />;
      case 'Table':
        return <TableIcon fillColor={fillColor} />;
      case 'Calendar':
        return <CalendarIcon fillColor={fillColor} />;
      default:
       return <HybridIcon fillColor={fillColor} />;
    }
  };

  const tabs: Array<TabType> = ['Hybrid', 'Table', 'Calendar'];

  return (
    <View style={[styles.container, isMobile && styles.containerMobile, isDark && { backgroundColor: colors.background.surface, shadowColor: 'transparent' }]}>
      {/* Title Section - Top on mobile, Left on desktop */}
      {isMobile && (
        <View style={styles.titleContainerMobile}>
          <Text style={[styles.title, isMobile && styles.titleMobile, isDark && { color: colors.text.primary }]}>
            {getTitle()}
          </Text>
        </View>
      )}
      
      {!isMobile && (
        <View style={styles.titleContainer}>
          <Text style={[styles.title, isMobile && styles.titleMobile, isDark && { color: colors.text.primary }]}>
            {getTitle()}
          </Text>
        </View>
      )}

      {/* Tab Control Section - Center */}
      <View
        style={[
          styles.tabsContainer,
          isMobile && styles.tabsContainerMobile,
          isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default },
        ]}
      >
        {tabs.map((tab, index) => {
          const isActive = activeTab === tab;
          const IconComponent = getIcon(tab, isActive);
          const isFirst = index === 0;
          const isLast = index === tabs.length - 1;

          return (
            <React.Fragment key={tab}>
              <TouchableOpacity
                onPress={() => handleTabChange(tab)}
                style={[
                  styles.tab,
                  isDark && { backgroundColor: colors.background.surface },
                  isActive && (isDark ? { backgroundColor: colors.brand.primary } : styles.tabActive),
                  isMobile && styles.tabMobile,
                  isFirst && styles.tabFirst,
                  isLast && styles.tabLast,
                ]}
                activeOpacity={0.7}
              >
                <View style={styles.iconWrapper}>
                  {IconComponent}
                </View>
                <Text
                  style={[
                    styles.tabLabel,
                    isDark && { color: colors.text.primary },
                    isActive && styles.tabLabelActive,
                    isMobile && styles.tabLabelMobile,
                  ]}
                >
                  {tab}
                </Text>
              </TouchableOpacity>

              {/* Divider between tabs */}
              {!isLast && <View style={[styles.tabDivider, isDark && { backgroundColor: colors.border.default }]} />}
            </React.Fragment>
          );
        })}
      </View>

      {/* Empty spacer - Right (for balance) */}
      <View style={styles.spacer} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    backgroundColor: '#FFFFFF',
    paddingHorizontal: Platform.OS === 'web' ? 8 : 8,
    paddingVertical: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 24,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    padding: 24,
    shadowColor: 'rgba(0,94,192,0.08)',
    shadowOffset: {width: 0, height: 6},
    shadowOpacity: 1,
    shadowRadius: 24,
    elevation: 4,
  },
  containerMobile: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
  },
  titleContainer: {
    flex: 1,
  },
  titleContainerMobile: {
    width: '100%',
    marginBottom: 8,
  },
  spacer: {
    flex: 1,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: '#2D2D2D',
    lineHeight: 32,
  },
  titleMobile: {
    fontSize: 20,
    lineHeight: 28,
  },
  tabsContainer: {
    flexDirection: 'row',
    backgroundColor: '#D8E7FB',
    borderRadius: 4,
    padding: 1,
    gap: 1,
    borderWidth: 1,
    borderColor: '#D8E7FB',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  tabsContainerMobile: {
    width: '100%',
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    gap: 6,
    backgroundColor: '#FFFFFF',
  },
  tabFirst: {
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
  },
  tabLast: {
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
  },
  tabDivider: {
    width: 1,
    backgroundColor: '#D8E7FB',
    height: '60%',
    alignSelf: 'center',
  },
  tabMobile: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 8,
  },
  tabActive: {
    backgroundColor: '#034895',
  },
  tabIcon: {
    marginRight: -2,
  },
  iconWrapper: {
    justifyContent: 'center',
    alignItems: 'center',
    width: 20,
    height: 20,
  },
  tabLabel: {
    fontSize: 14,
    fontWeight: '400',
    color: '#034895',
  },
  tabLabelMobile: {
    fontSize: 12,
  },
  tabLabelActive: {
    fontWeight: '700',
    color: Platform.OS === 'ios' ? '#FFFFFF' : (Platform.OS === 'web' ? '#FFFFFF' : '#FFFFFF'),
  },
});

export default ActionPlanDashboardTitle;

